// components/MessageMedia.js

const DownloadIcon = () => (
  <svg viewBox="0 0 24 24" width="22" height="22" fill="currentColor">
    <path d="M5 20h14v-2H5v2zm7-18v12.17l3.59-3.58L17 12l-5 5-5-5 1.41-1.41L11 13.17V2h2z" />
  </svg>
);

const formatFileSize = (bytes) => {
  if (!bytes) return "";
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} kB`;
  return `${(bytes / 1024 / 1024).toFixed(2)} MB`;
};

/* ── Reply-preview helpers (internal, not exported) ── */
function getReplyMediaKind(media) {
  if (!media) return null;
  const mime = media.mimeType || "";
  if (mime.startsWith("image/")) return "image";
  if (mime.startsWith("video/")) return "video";
  if (media.type === "image" || media.type === "gif") return "image";
  if (media.type === "video") return "video";
  return null;
}

function getReplyMediaLabel(media) {
  const kind = getReplyMediaKind(media);
  if (kind === "image") return "📷 Photo";
  if (kind === "video") return "🎥 Video";
  return "📎 Media";
}

function ReplyThumb({ media, size = 40, className = "" }) {
  const kind = getReplyMediaKind(media);
  if (!media?.url || !kind) return null;

  return (
    <div
      className={`relative flex-shrink-0 overflow-hidden bg-neutral-900 ${className}`}
      style={{ width: size, height: size }}
    >
      {kind === "image" ? (
        <img src={media.url} alt="" className="h-full w-full object-cover" />
      ) : (
        <>
          <video
            src={`${media.url}#t=0.1`}
            muted
            playsInline
            preload="metadata"
            controls={false}
            autoPlay={false}
            className="h-full w-full object-cover pointer-events-none"
          />
          <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
            <div className="flex h-5 w-5 items-center justify-center rounded-full bg-black/55 text-white">
              <svg viewBox="0 0 24 24" width="12" height="12" fill="currentColor">
                <path d="M8 5v14l11-7z" />
              </svg>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

// ── isRevealed / onReveal are props ──
// variant: "full" (default, chat bubble) | "replyThumb" | "replyLabel"
export default function MessageMedia({
  media,
  onOpen,
 isRevealed = true,
  onReveal = () => {},
  variant = "full",
  size = 40,
  className = "",
}) {
  if (variant === "replyLabel") return getReplyMediaLabel(media);
  if (variant === "replyThumb")
    return <ReplyThumb media={media} size={size} className={className} />;

  if (!media?.url) return null;

  const mimeType = media.mimeType || "";
  const type = mimeType.startsWith("audio/")
    ? "audio"
    : mimeType.startsWith("image/")
    ? "image"
    : mimeType.startsWith("video/")
    ? "video"
    : media.type || "document";

  /* IMAGE / GIF
     ── one <img> node for the whole lifecycle of this message.
     Reveal only toggles the blur/overlay classes, it never swaps
     to a different element tree — so React keeps the same DOM
     node (and the browser keeps the already-loaded image) instead
     of unmounting/remounting and re-fetching on every reveal. */
  if (type === "image" || type === "gif") {
    return (
      <div
        className="relative mt-1 mb-1.5 overflow-hidden rounded-lg cursor-pointer bg-neutral-900"
        style={{
          maxWidth: "min(240px, 60vw)",
          minHeight: isRevealed ? undefined : "160px",
        }}
        onClick={isRevealed ? onOpen : onReveal}
      >
        <img
          src={media.url}
          alt={media.fileName || "image"}
          className="w-full h-auto block rounded-lg transition-[filter,transform] duration-200"
          style={{
            maxHeight: "300px",
            minHeight: isRevealed ? undefined : "160px",
            objectFit: "cover",
            filter: isRevealed ? "none" : "blur(14px)",
            transform: isRevealed ? "none" : "scale(1.08)",
          }}
        />

        {!isRevealed && (
          <>
            <div className="absolute inset-0 bg-black/40" />
            <div className="absolute inset-0 z-10 flex flex-col items-center justify-center gap-1.5 text-white">
              <DownloadIcon />
              {formatFileSize(media.fileSize) ? (
                <span className="text-xs font-medium">
                  {formatFileSize(media.fileSize)}
                </span>
              ) : null}
            </div>
          </>
        )}
      </div>
    );
  }

  /* VIDEO
   ── A single <video> node is kept for the entire lifecycle of the message.
   The thumbnail stays clear (no blur), always shows a centered play button,
   never auto-plays in chat, and tapping the revealed video opens the
   full-screen lightbox. */
if (type === "video") {
  return (
    <div
      className="relative mt-1 mb-1.5 overflow-hidden rounded-lg cursor-pointer bg-neutral-900"
      style={{
        maxWidth: "min(240px, 60vw)",
        minHeight: isRevealed ? undefined : "160px",
      }}
      onClick={isRevealed ? onOpen : onReveal}
    >
      <video
        src={media.url}
        muted
        playsInline
        preload="metadata"
        controls={false}
        autoPlay={false}
        className="w-full h-auto block rounded-lg"
        style={{
          maxHeight: "300px",
          minHeight: isRevealed ? undefined : "160px",
          objectFit: "cover",
          filter: "none",
          transform: "none",
        }}
      />

      {/* Dark overlay only before reveal */}
      {!isRevealed && (
        <div className="absolute inset-0 bg-black/35 pointer-events-none" />
      )}

      {/* Always-visible play button */}
      <div className="absolute inset-0 z-10 flex flex-col items-center justify-center gap-2 text-white pointer-events-none">
        <div className="w-14 h-14 rounded-full bg-black/55 flex items-center justify-center shadow-lg">
          <svg viewBox="0 0 24 24" width="28" height="28" fill="currentColor">
            <path d="M8 5v14l11-7z" />
          </svg>
        </div>

        {/* File size only before reveal */}
        {!isRevealed && formatFileSize(media.fileSize) ? (
          <span className="text-xs font-medium">
            {formatFileSize(media.fileSize)}
          </span>
        ) : null}
      </div>

      {/* Duration badge */}
      {media.duration && (
        <span className="absolute bottom-1.5 left-2 z-20 text-[11px] text-white bg-black/50 px-1.5 py-0.5 rounded pointer-events-none">
          {media.duration}
        </span>
      )}
    </div>
  );
}

  /* AUDIO */
  if (type === "audio") {
    return (
      <div className="mt-1 mb-1.5 w-full max-w-[220px]">
        <audio
          src={media.url}
          className="w-full"
          controls
          style={{ height: "36px" }}
        />
      </div>
    );
  }

  /* DOCUMENT */
  return (
    <div
      className="mt-1 mb-1.5 flex items-center gap-2.5 px-3 py-2.5 rounded-lg bg-black/5 dark:bg-white/10 cursor-pointer hover:bg-black/10 dark:hover:bg-white/15 touch-manipulation transition-colors"
      style={{ maxWidth: "min(220px, 60vw)" }}
      onClick={onOpen}
    >
      <div className="text-2xl shrink-0">📄</div>

      <div className="min-w-0">
        <div className="text-sm font-medium truncate">
          {media.fileName || "Document"}
        </div>

        {media.fileSize && (
          <div className="text-xs text-neutral-500 mt-0.5">
            {(media.fileSize / 1024 / 1024).toFixed(2)} MB
          </div>
        )}
      </div>
    </div>
  );
}